<template>
  <!-- Delete Member Confirmation Modal -->
  <div class="modal fade"
       :id="modalId"
       tabindex="-1"
       :aria-labelledby="modalId + 'Label'"
       aria-hidden="true"
       ref="deleteModal">
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header bg-danger text-white">
          <h5 class="modal-title" :id="modalId + 'Label'" style="color: white">
            <i class="bx bx-trash me-2"></i>Delete Member Account
          </h5>
          <button type="button"
                  class="btn-close btn-close-white"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                  @click="closeModal"></button>
        </div>
        <div class="modal-body">
          <div class="text-center mb-4">
            <i class="bx bx-error-circle display-1 text-danger"></i>
          </div>

          <div class="alert alert-danger">
            <h6 class="alert-heading">
              <i class="bx bx-info-circle me-2"></i>Critical Action Warning
            </h6>
            <p class="mb-2">You are about to permanently delete:</p>
            <ul class="mb-0">
              <li><strong>Member:</strong> {{ memberFullName }}</li>
              <li><strong>CBHI Number:</strong> {{ cbhiNumber }}</li>
              <li><strong>Total Beneficiaries:</strong> {{ totalBeneficiaries }}</li>
            </ul>
          </div>

          <div class="alert alert-warning">
            <strong>This action will:</strong>
            <ul class="mb-0 mt-2">
              <li>Permanently delete the member record</li>
              <li>Remove all {{ beneficiaryCount }} beneficiaries</li>
              <li>Archive all payment history</li>
              <li>Archive all claims history</li>
              <li>Cannot be undone without admin intervention</li>
            </ul>
          </div>

          <div class="form-group mt-3">
            <label class="form-label">
              <strong>Type "DELETE" to confirm:</strong>
            </label>
            <input
                type="text"
                class="form-control"
                v-model="deleteConfirmation"
                placeholder="Type DELETE to confirm"
                @input="deleteConfirmation = deleteConfirmation.toUpperCase()"
                @keyup.enter="confirmDelete"
                :disabled="deleting"
            >
          </div>

          <!-- Error Alert -->
          <div v-if="errorMessage" class="alert alert-danger mt-3">
            <i class="bx bx-error me-2"></i>{{ errorMessage }}
          </div>
        </div>
        <div class="modal-footer">
          <button type="button"
                  class="btn btn-secondary"
                  data-bs-dismiss="modal"
                  @click="closeModal"
                  :disabled="deleting">
            <i class="bx bx-x me-1"></i> Cancel
          </button>
          <button
              type="button"
              class="btn btn-danger"
              @click="confirmDelete"
              :disabled="deleteConfirmation !== 'DELETE' || deleting"
          >
            <span v-if="deleting">
              <span class="spinner-border spinner-border-sm me-2"></span>
              Deleting...
            </span>
            <span v-else>
              <i class="bx bx-trash me-1"></i> Delete Member
            </span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { authUrl, executeJson } from "@/api";

export default {
  name: 'DeleteMemberModal',

  props: {
    // Changed from memberId to cbhiNumber
    cbhiNumber: {
      type: String,
      required: true
    },
    memberInfo: {
      type: Object,
      default: () => ({})
    },
    beneficiaryCount: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      modalId: 'deleteMemberModal',
      modalInstance: null,
      deleteConfirmation: '',
      deleting: false,
      errorMessage: ''
    };
  },

  computed: {
    memberFullName() {
      if (!this.memberInfo) return 'Unknown Member';

      // Handle different possible property formats
      const firstName = this.memberInfo.first_name || this.memberInfo.firstName || '';
      const middleName = this.memberInfo.middle_name || this.memberInfo.middleName || '';
      const surname = this.memberInfo.surname || this.memberInfo.lastName || '';

      return `${firstName} ${middleName} ${surname}`.trim();
    },

    totalBeneficiaries() {
      return 1 + this.beneficiaryCount; // Primary member + beneficiaries
    }
  },

  mounted() {
    this.initializeModal();
  },

  beforeUnmount() {
    this.destroyModal();
  },

  methods: {
    // Initialize Bootstrap modal
    initializeModal() {
      this.$nextTick(() => {
        try {
          if (typeof window !== 'undefined' && window.bootstrap && window.bootstrap.Modal) {
            const modalElement = this.$refs.deleteModal;
            if (modalElement) {
              this.modalInstance = new window.bootstrap.Modal(modalElement, {
                keyboard: true,
                backdrop: 'static',
                focus: true
              });

              // Listen for modal hidden event
              modalElement.addEventListener('hidden.bs.modal', this.handleModalHidden);
            }
          }
        } catch (error) {
          console.error('Error initializing delete modal:', error);
        }
      });
    },

    // Destroy modal instance
    destroyModal() {
      try {
        if (this.modalInstance) {
          const modalElement = this.$refs.deleteModal;
          if (modalElement) {
            modalElement.removeEventListener('hidden.bs.modal', this.handleModalHidden);
          }
          this.modalInstance.dispose();
          this.modalInstance = null;
        }
      } catch (error) {
        console.error('Error destroying modal:', error);
      }
    },

    // Open the modal
    openModal() {
      // Reset state
      this.deleteConfirmation = '';
      this.deleting = false;
      this.errorMessage = '';

      // Show modal
      if (this.modalInstance) {
        this.modalInstance.show();
      } else {
        // Fallback to manual show
        this.showModalManually();
      }
    },

    // Close the modal
    closeModal() {
      if (!this.deleting) {
        if (this.modalInstance) {
          this.modalInstance.hide();
        } else {
          this.hideModalManually();
        }
      }
    },

    // Handle modal hidden event
    handleModalHidden() {
      this.deleteConfirmation = '';
      this.errorMessage = '';
      this.deleting = false;
    },

    // Manual modal show (fallback)
    showModalManually() {
      try {
        const modalEl = this.$refs.deleteModal;
        if (modalEl) {
          modalEl.style.display = 'block';
          modalEl.classList.add('show');
          modalEl.setAttribute('aria-modal', 'true');
          modalEl.setAttribute('role', 'dialog');
          modalEl.removeAttribute('aria-hidden');

          // Add backdrop
          let backdrop = document.querySelector('.modal-backdrop');
          if (!backdrop) {
            backdrop = document.createElement('div');
            backdrop.className = 'modal-backdrop fade show';
            document.body.appendChild(backdrop);
          }

          document.body.classList.add('modal-open');
        }
      } catch (error) {
        console.error('Error showing modal manually:', error);
        this.showBrowserConfirm();
      }
    },

    // Manual modal hide (fallback)
    hideModalManually() {
      try {
        const modalEl = this.$refs.deleteModal;
        if (modalEl) {
          modalEl.style.display = 'none';
          modalEl.classList.remove('show');
          modalEl.setAttribute('aria-hidden', 'true');
          modalEl.removeAttribute('aria-modal');
          modalEl.removeAttribute('role');
        }

        const backdrop = document.querySelector('.modal-backdrop');
        if (backdrop) {
          backdrop.remove();
        }

        document.body.classList.remove('modal-open');
      } catch (error) {
        console.error('Error hiding modal manually:', error);
      }
    },

    // Browser confirm fallback
    showBrowserConfirm() {
      const confirmMessage = `Are you sure you want to delete ${this.memberFullName}?\n\n` +
          `CBHI Number: ${this.cbhiNumber}\n` +
          `Total Beneficiaries: ${this.totalBeneficiaries}\n\n` +
          `This action cannot be undone!`;

      if (confirm(confirmMessage)) {
        this.deleteConfirmation = 'DELETE';
        this.confirmDelete();
      }
    },

    // Confirm and execute deletion
    async confirmDelete() {
      if (this.deleteConfirmation !== 'DELETE') {
        this.errorMessage = 'Please type "DELETE" to confirm';
        return;
      }

      this.deleting = true;
      this.errorMessage = '';

      // Using cbhiNumber instead of memberId
      const data = {
        function: 'deleteMember',
        cbhiNumber: this.cbhiNumber,  // Changed from memberId
        reason: 'User initiated deletion from member details page'
      };

      try {
        const res = await executeJson(data, authUrl);

        if (res.data.success) {
          // Emit success event
          this.$emit('member-deleted', {
            success: true,
            message: res.data.message || 'Member deleted successfully',
            cbhiNumber: this.cbhiNumber
          });

          // Close modal
          this.closeModal();
        } else {
          this.errorMessage = res.data.message || 'Failed to delete member';
          this.deleting = false;
        }
      } catch (error) {
        console.error('Delete member error:', error);
        this.errorMessage = error.message || 'An error occurred while deleting member';
        this.deleting = false;
      }
    }
  }
};
</script>

<style scoped>
.modal-header.bg-danger {
  background-color: #dc3545 !important;
}

.modal-header.bg-danger .modal-title {
  color: white !important;
}

.btn-close-white {
  filter: invert(1) grayscale(100%) brightness(200%);
}

.display-1 {
  font-size: 5rem;
}

.alert {
  border-radius: 0.375rem;
}

.alert-danger {
  background-color: #f8d7da;
  border-color: #f5c6cb;
  color: #721c24;
}

.alert-warning {
  background-color: #fff3cd;
  border-color: #ffeeba;
  color: #856404;
}

.form-control:disabled {
  background-color: #e9ecef;
  opacity: 1;
}

.spinner-border-sm {
  width: 1rem;
  height: 1rem;
  border-width: 0.2em;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
}

/* Ensure modal appears above other content */
.modal {
  z-index: 1055;
}

.modal-backdrop {
  z-index: 1050;
}

/* Responsive adjustments */
@media (max-width: 576px) {
  .modal-dialog {
    margin: 1rem;
  }

  .display-1 {
    font-size: 3rem;
  }
}
</style>